import { GraphQLReturn, graphQLSelector } from '@cherre-frontend/data-fetching';
import { graphql } from 'react-relay';
import { monitorSubmissionsPropertyBatchWhereClause } from '../../recoil';

import { recoilPropertyBatchesKPIQuery } from './__generated__/recoilPropertyBatchesKPIQuery.graphql';
import { recoilPropertyBatchesFinancialKPIQuery } from './__generated__/recoilPropertyBatchesFinancialKPIQuery.graphql';
import { recoilPropertyBatchesOperationalKPIQuery } from './__generated__/recoilPropertyBatchesOperationalKPIQuery.graphql';

export const getPropertyBatchesKPIQuery = graphQLSelector({
  query: graphql`
    query recoilPropertyBatchesKPIQuery(
      $where_clause: sys_property_batches_bool_exp!
    ) {
      sys_property_batches_monitoring_aggregate(
        where: { property_batch: $where_clause }
      ) {
        aggregate {
          count
          avg {
            cycle_time
          }
          sum {
            datasets_total
            datasets_uploaded
            dataset_validations_failed
            validations_failed
            stuck_count
            rejected_count
            past_due_count
            approved_count
          }
        }
      }
    }
  ` as GraphQLReturn<recoilPropertyBatchesKPIQuery>,
  mapVariables:
    () =>
    ({ get }) => {
      const where_clause = get(monitorSubmissionsPropertyBatchWhereClause({}));

      return {
        where_clause,
      };
    },
  mapResponse: (response) => ({
    property_submissions:
      response.sys_property_batches_monitoring_aggregate.aggregate?.count ?? 0,
    total_datasets:
      response.sys_property_batches_monitoring_aggregate.aggregate?.sum
        ?.datasets_total ?? 0,
    uploaded_datasets:
      response.sys_property_batches_monitoring_aggregate.aggregate?.sum
        ?.datasets_uploaded ?? 0,
    failed_validations:
      (response.sys_property_batches_monitoring_aggregate.aggregate?.sum
        ?.dataset_validations_failed ?? 0) +
      (response.sys_property_batches_monitoring_aggregate.aggregate?.sum
        ?.validations_failed ?? 0),
    avg_property_cycle_time:
      response.sys_property_batches_monitoring_aggregate.aggregate?.avg
        ?.cycle_time ?? 0,
    stuck_properties:
      response.sys_property_batches_monitoring_aggregate.aggregate?.sum
        ?.stuck_count ?? 0,
    rejected_properties:
      response.sys_property_batches_monitoring_aggregate.aggregate?.sum
        ?.rejected_count ?? 0,
    past_due_properties:
      response.sys_property_batches_monitoring_aggregate.aggregate?.sum
        ?.past_due_count ?? 0,
    approved_properties:
      response.sys_property_batches_monitoring_aggregate.aggregate?.sum
        ?.approved_count ?? 0,
  }),
});

export const getPropertyBatchesFinancialKPIQuery = graphQLSelector({
  query: graphql`
    query recoilPropertyBatchesFinancialKPIQuery(
      $where_clause: sys_property_batches_bool_exp!
    ) {
      sys_property_batches_monitoring_aggregate(
        where: { property_batch: $where_clause }
      ) {
        aggregate {
          sum {
            datasets_total
            datasets_uploaded
          }
        }
      }
    }
  ` as GraphQLReturn<recoilPropertyBatchesFinancialKPIQuery>,
  mapVariables:
    () =>
    ({ get }) => {
      const where_clause = get(
        monitorSubmissionsPropertyBatchWhereClause({
          submission: {
            submission_type: { submission_type_slug: { _eq: 'financial' } },
          },
        })
      );
      return {
        where_clause,
      };
    },
  mapResponse: (response) => ({
    total_datasets:
      response.sys_property_batches_monitoring_aggregate.aggregate?.sum
        ?.datasets_total ?? 0,
    uploaded_datasets:
      response.sys_property_batches_monitoring_aggregate.aggregate?.sum
        ?.datasets_uploaded ?? 0,
  }),
});

export const getPropertyBatchesOperationalKPIQuery = graphQLSelector({
  query: graphql`
    query recoilPropertyBatchesOperationalKPIQuery(
      $where_clause: sys_property_batches_bool_exp!
    ) {
      sys_property_batches_monitoring_aggregate(
        where: { property_batch: $where_clause }
      ) {
        aggregate {
          sum {
            datasets_total
            datasets_uploaded
          }
        }
      }
    }
  ` as GraphQLReturn<recoilPropertyBatchesOperationalKPIQuery>,
  mapVariables:
    () =>
    ({ get }) => {
      const where_clause = get(
        monitorSubmissionsPropertyBatchWhereClause({
          submission: {
            submission_type: { submission_type_slug: { _eq: 'operational' } },
          },
        })
      );
      return {
        where_clause,
      };
    },
  mapResponse: (response) => ({
    total_datasets:
      response.sys_property_batches_monitoring_aggregate.aggregate?.sum
        ?.datasets_total ?? 0,
    uploaded_datasets:
      response.sys_property_batches_monitoring_aggregate.aggregate?.sum
        ?.datasets_uploaded ?? 0,
  }),
});
